import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import {
  useGetWelcomeDmQuery,
  useTestWelcomeDmMutation,
  useUpdateWelcomeDmMutation,
} from '../../redux/adminApiSlice';

const WelcomeDmPanel: React.FC = () => {
  const { data, isLoading, error, refetch } = useGetWelcomeDmQuery();
  const [updateWelcomeDm, updateState] = useUpdateWelcomeDmMutation();
  const [testWelcomeDm, testState] = useTestWelcomeDmMutation();

  const [enabled, setEnabled] = useState(false);
  const [senderUserId, setSenderUserId] = useState('');
  const [message, setMessage] = useState('');
  const [testUserId, setTestUserId] = useState('');
  const [status, setStatus] = useState<string | null>(null);

  useEffect(() => {
    if (!data) return;
    setEnabled(!!data.enabled);
    setSenderUserId(data.senderUserId || '');
    setMessage(data.message || '');
  }, [data]);

  const onSave = async () => {
    setStatus(null);
    try {
      await updateWelcomeDm({ enabled, senderUserId, message }).unwrap();
      setStatus('Welcome DM settings saved.');
      void refetch();
    } catch (err: unknown) {
      const e = err as { data?: { message?: string }; message?: string };
      setStatus(e?.data?.message || e?.message || 'Save failed');
    }
  };

  const onTest = async () => {
    setStatus(null);
    try {
      const result = await testWelcomeDm({ userId: testUserId.trim() }).unwrap();
      setStatus(
        result.sent
          ? `Test welcome DM sent to ${testUserId}.`
          : `No DM sent (already have a conversation with that user, or config incomplete).`
      );
    } catch (err: unknown) {
      const e = err as { data?: { message?: string }; message?: string };
      setStatus(e?.data?.message || e?.message || 'Test send failed');
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">Failed to load welcome DM settings.</Alert>;
  }

  return (
    <Stack spacing={2} maxWidth={720}>
      <Typography variant="h6">Welcome DM</Typography>
      <Typography color="text.secondary">
        When enabled, every new registrant gets an already-accepted DM from the sender account
        (your user). Configure your Mongo userId as sender, write the message, then turn it on.
      </Typography>

      <FormControlLabel
        control={<Switch checked={enabled} onChange={(_, v) => setEnabled(v)} />}
        label="Auto-send welcome DM to new users"
      />

      <TextField
        label="Sender userId"
        value={senderUserId}
        onChange={(e) => setSenderUserId(e.target.value)}
        helperText={
          data?.sender
            ? `Resolved: @${data.sender.username}${data.sender.displayName ? ` (${data.sender.displayName})` : ''}`
            : 'Paste your Grid user ObjectId'
        }
        fullWidth
      />

      <TextField
        label="Welcome message"
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        multiline
        minRows={4}
        fullWidth
      />

      <Box>
        <Button
          variant="contained"
          onClick={() => void onSave()}
          disabled={updateState.isLoading}
        >
          {updateState.isLoading ? 'Saving…' : 'Save'}
        </Button>
      </Box>

      <Typography variant="subtitle1" sx={{ pt: 2 }}>
        Test send
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
        <TextField
          label="Recipient userId"
          value={testUserId}
          onChange={(e) => setTestUserId(e.target.value)}
          fullWidth
        />
        <Button
          variant="outlined"
          onClick={() => void onTest()}
          disabled={!testUserId.trim() || testState.isLoading}
          sx={{ whiteSpace: 'nowrap' }}
        >
          {testState.isLoading ? 'Sending…' : 'Send test'}
        </Button>
      </Stack>

      {status ? <Alert severity="info">{status}</Alert> : null}
    </Stack>
  );
};

export default WelcomeDmPanel;
